/**
 * FinanceChatEmbed.tsx
 * Embeds chat.core-loop.dev in the finance app with the "finance" tool scope.
 *
 * Replaces the legacy ChatPanel which called llm.core-loop.dev directly
 * (broken due to CORS + auth complexity). Routes through chat-api which
 * has proper auth, CORS, tool support, and thread persistence.
 *
 * The embed is advisory: the LLM explains scenarios and suggests parameter
 * changes as JSON blocks the user can copy into the simulator controls.
 * Direct param mutation (postMessage bridge) is Phase 2.
 */

import { useEffect, useRef, useState } from 'react'

const CHAT_BASE_URL = import.meta.env.VITE_CHAT_URL ?? 'https://chat.core-loop.dev'
const READY_TIMEOUT_MS = 10000

/** Finance-specific quick-start prompts shown before the user sends their first message. */
const FINANCE_SUGGESTIONS = [
  { label: 'Buy a home', prompt: 'Simulate buying a $500k home with 20% down.' },
  { label: 'Job loss', prompt: 'Simulate losing my job completely for a year.' },
  { label: 'Market crash (35%)', prompt: 'Simulate a major market crash — 35% portfolio drop and slower recovery.' },
  { label: 'Early retirement', prompt: 'What if I retired 10 years early?' },
  { label: 'Windfall $100k', prompt: 'Simulate a $100k windfall or inheritance.' },
  { label: 'On track?', prompt: 'Am I on track for retirement based on my current numbers?' },
]

interface Props {
  activeScenario: string | null
  hidden?: boolean
  /** When true the user is signed in to the finance app (shared .core-loop.dev cookie).
   *  We keep trying longer and never show the sign-in fallback. */
  isAuthenticated?: boolean
}

function toSlug(s: string | null): string {
  if (!s) return 'general';
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 80) || 'general';
}

export function FinanceChatEmbed({ activeScenario, hidden, isAuthenticated }: Props) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'fallback'>('loading');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Show suggestion chips until the user sends their first message
  const [chipsVisible, setChipsVisible] = useState(true);

  const slug = toSlug(activeScenario);
  const chatParams = new URLSearchParams({ context: 'finance', slug });
  const src = `${CHAT_BASE_URL}?${chatParams.toString()}`;

  // When authenticated: wait longer (30s) -- the shared cookie means the chat
  // embed WILL authenticate, it just might take a few extra seconds.
  // When not authenticated: show the sign-in prompt after 10s.
  const timeoutMs = isAuthenticated ? 30000 : READY_TIMEOUT_MS;

  // Attempt (or re-attempt) loading the iframe. Resets state to 'loading' and
  // starts the fallback timeout. Called on mount, slug change, and tab focus.
  const attemptLoad = (url: string) => {
    setStatus('loading');
    if (timerRef.current) clearTimeout(timerRef.current);
    if (iframeRef.current) iframeRef.current.src = url;
    timerRef.current = setTimeout(() => setStatus('fallback'), timeoutMs);
  };

  // Reset and reload when scenario changes; reset chips so they show again
  useEffect(() => {
    setChipsVisible(true);
    attemptLoad(src);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  // When the tab regains focus (user returning from the sign-in tab), retry.
  useEffect(() => {
    function handleFocus() {
      if (status === 'fallback') attemptLoad(src);
    }
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, src]);

  // When the user signs in to finance (isAuthenticated goes true), immediately
  // retry the iframe. The shared .core-loop.dev cookie means the chat embed
  // will now authenticate automatically without a separate sign-in step.
  const prevAuth = useRef(isAuthenticated);
  useEffect(() => {
    if (isAuthenticated && !prevAuth.current) {
      attemptLoad(src);
    }
    prevAuth.current = isAuthenticated;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Listen for the chat-embed-ready postMessage from the chat iframe.
  // This fires only when the user is authenticated AND the thread loaded.
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.origin !== CHAT_BASE_URL) return;
      if (event.data?.type === 'chat-embed-ready') {
        if (timerRef.current) clearTimeout(timerRef.current);
        setStatus('ready');
      }
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  /** Inject a prompt into the chat iframe and hide the chips. */
  const injectPrompt = (text: string) => {
    setChipsVisible(false);
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'chat-inject-message', text },
      CHAT_BASE_URL
    );
  };

  /** Restart the chat by creating a new embed thread (new slug with timestamp). */
  const handleRestart = () => {
    setChipsVisible(true);
    // Generate new slug with timestamp to force a fresh thread lookup
    const newSlug = toSlug(activeScenario) + '-' + Date.now();
    const newParams = new URLSearchParams({ context: 'finance', slug: newSlug });
    const newSrc = `${CHAT_BASE_URL}?${newParams.toString()}`;
    attemptLoad(newSrc);
  };

  return (
    <div
      className={`chat-panel${hidden ? ' chat-panel--mobile-hidden' : ''}`}
      aria-label="Financial planning chat assistant"
      style={{ position: 'relative' }}
    >
      {/* Loading spinner */}
      {status === 'loading' && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, color: 'var(--gray-500)', fontSize: 13, padding: 20, zIndex: 1 }}>
          <span style={{ fontSize: 24 }}>💬</span>
          <span>Loading AI assistant…</span>
        </div>
      )}

      {/* Quick-start suggestion chips — shown over the ready iframe when thread is fresh */}
      {status === 'ready' && chipsVisible && (
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          padding: '10px 12px',
          background: 'var(--fin-bg-panel, var(--bg-panel))',
          borderTop: '1px solid var(--fin-border, var(--surface-border-soft))',
          zIndex: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <p style={{ margin: 0, fontSize: 11, color: 'var(--gray-500)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Quick scenarios
            </p>
            <button
              onClick={handleRestart}
              title="Clear conversation and start fresh"
              style={{
                background: 'transparent',
                border: '1px solid var(--fin-border, #ddd)',
                borderRadius: 12,
                padding: '2px 8px',
                fontSize: 11,
                cursor: 'pointer',
                color: 'var(--gray-500)',
                fontFamily: 'inherit',
                fontWeight: 500,
              }}
            >
              🔄 Restart
            </button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {FINANCE_SUGGESTIONS.map(s => (
              <button
                key={s.label}
                onClick={() => injectPrompt(s.prompt)}
                style={{
                  background: 'var(--surface-card-strong, #f5f5f5)',
                  border: '1px solid var(--fin-border, #ddd)',
                  borderRadius: 14,
                  padding: '4px 12px',
                  fontSize: 12,
                  cursor: 'pointer',
                  color: 'var(--ink-soft)',
                  fontFamily: 'inherit',
                  whiteSpace: 'nowrap',
                }}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Sign-in fallback when postMessage never arrived (unauthenticated) */}
      {status === 'fallback' && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, padding: 24, textAlign: 'center', color: 'var(--gray-600)', zIndex: 1 }}>
          <span style={{ fontSize: 32 }}>🤖</span>
          <p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: 'var(--gray-800)' }}>AI financial assistant</p>
          <p style={{ margin: 0, fontSize: 12, lineHeight: 1.5 }}>Sign in to enable the AI assistant. It can help you model scenarios, run retirement checks, and explain simulation changes.</p>
          <a
            href={`${CHAT_BASE_URL}?context=finance&slug=${slug}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ background: 'var(--blue-600)', color: '#fff', borderRadius: 6, padding: '8px 18px', fontSize: 13, fontWeight: 600, textDecoration: 'none', display: 'inline-block' }}
          >
            Sign in →
          </a>
        </div>
      )}

      {/* The iframe — always mounted so it can attempt auth; opacity hidden until ready */}
      <iframe
        ref={iframeRef}
        src={src}
        title={`Financial planning chat${activeScenario ? ` - ${activeScenario}` : ''}`}
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          position: 'absolute',
          inset: 0,
          opacity: status === 'ready' ? 1 : 0,
          pointerEvents: status === 'ready' ? 'auto' : 'none',
          zIndex: status === 'ready' ? 2 : 0,
        }}
        aria-label="Chat assistant for financial scenario planning"
        aria-hidden={status !== 'ready'}
      />

      {/* Floating restart button — visible when chat is ready */}
      {status === 'ready' && !chipsVisible && (
        <button
          onClick={handleRestart}
          title="Clear conversation and start fresh"
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            background: 'rgba(255, 255, 255, 0.9)',
            border: '1px solid var(--fin-border, #ddd)',
            borderRadius: 8,
            padding: '4px 10px',
            fontSize: 12,
            cursor: 'pointer',
            color: 'var(--ink-soft)',
            fontFamily: 'inherit',
            fontWeight: 500,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            backdropFilter: 'blur(2px)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          🔄
        </button>
      )}
    </div>
  )
}
